import {observer} from 'mobx-react-lite'
import React, {useEffect, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {logger, Util} from 'yougile-util'
import {Hub} from 'yougile-model'
import {Backend} from '@common/backend'
import {Tracking} from '@common/tracking'
import {TrackingEvent} from '@common/tracking-events'
import {LinkButton} from '@components/common/base/buttons'
import {Button} from '@components/common/button/button'
import {Loading} from '@components/common/loading/loading'
import {Popup} from '@components/common/popup/popup'
import {ProgressBar} from '@components/common/progress-bar/progress-bar'
import {globals} from '@extensions/globals-base'
import {PageService} from '@extensions/page-service'
import {
  ImportFromTrelloContext,
  ImportFromTrelloStore,
  useImportFromTrelloStore
} from './import-from-trello-store'
import SelectedIcon from './selected.svg'
import {createTrelloProject} from './utils/create-trello-project'
import {
  fetchBoardFullName,
  fetchBoardOptions,
  trelloReAuthRequired
} from './utils/trello-fetch'
import {authenticateTrello} from './utils/trello-loader'

declare global {
  interface Window {
    Trello: {
      authorized: () => boolean
      authorize: (AuthOpts: {
        type: 'popup'
        name: 'YouGile'
        scope: {read: 'true'}
        expiration: '1hour'
        success?: () => void
        error?: (error: Error) => void
      }) => void
      deauthorize: () => void
      key: () => string | null
      token: () => string | null
    }
    opts: {
      authEndpoint: string
      apiEndpoint: string
      version: number
      key: string
    }
  }
}

const TwListItem = tw.div<{$selected: boolean}>`
  ${p => (p.$selected ? 'border-accent-old border-solid border-3' : undefined)}
  rounded-8
  cursor-pointer
  p-8
  pr-34
  h-96
  w-184
  mb-8
  mr-8
  relative
  text-16
  leading-5
  text-on-color
  font-semibold
  box-border
  overflow-hidden
  bg-menu-active-element-old
`

const TwCenter = tw.div`
  flex
  justify-center
  text-third-old
  mt-8
`

export const ImportFromTrello = ({
  isOpen,
  setIsOpen
}: {
  isOpen: boolean
  setIsOpen: (state: boolean) => void
}) => {
  if (Util.isDesktop() || globals.standalone) {
    return null
  }
  const [store] = useState(() => new ImportFromTrelloStore(setIsOpen))
  useEffect(() => {
    if (isOpen) {
      store.open()
    }
    store.setOpenState(isOpen)
  }, [isOpen])
  return (
    <ImportFromTrelloContext.Provider value={store}>
      <ImportFromTrelloBase />
    </ImportFromTrelloContext.Provider>
  )
}

const ImportFromTrelloBase = observer(() => {
  const [state, store] = useImportFromTrelloStore()
  const [project, setProject] = useState<Hub>()
  const {boards} = state

  useEffect(() => {
    if (state.open && state.error?.name === trelloReAuthRequired) {
      window.Trello?.deauthorize()
      store.setLoading(false)
      store.setBoards(undefined)
      store.setFullName('')
      store.setAuthenticated(false)
      authenticateTrello(store.onSuccess, store.onError)
    }
  }, [state.open, state.error])

  useEffect(() => {
    if (!state.open) {
      store.setLoading(false)
      setProject(undefined)
    }
  }, [state.open])

  useEffect(() => {
    if (!boards) {
      return
    }
    const unselected =
      Backend.loadLocal(TRELLO_UNSELECTED_KEY)?.split(',') ?? []
    store.setSelectedBoards(
      boards.map(x => x.value).filter(board => !unselected.includes(board))
    )
  }, [boards])

  useEffect(() => {
    if (state.open && state.authenticated) {
      store.setLoading(true)

      const run = async () => {
        store.setFullName(await fetchBoardFullName())
        store.setBoards(await fetchBoardOptions())
      }
      void run()
        .catch(e => {
          logger.error('Failed to load boards', e)
          e.message = `${Lang.msg.importFromTrello.failedToLoadBoards}: ${e.message}`
          store.setError(e)
        })
        .then(() => {
          store.setLoading(false)
        })
    }
  }, [state.open, state.authenticated])

  const onImportFromTrelloClick = async () => {
    if (!boards) {
      return
    }

    store.setLoading(true)

    const run = async () => {
      const boardsSelected = boards.filter(b =>
        state.selectedBoards.includes(b.value)
      )
      const boardsUnselected = boards
        .filter(b => !state.selectedBoards.includes(b.value))
        .map(b => b.value)
      void Tracking.send(TrackingEvent.trelloImport)
      Backend.saveLocal(TRELLO_UNSELECTED_KEY, boardsUnselected.join(','))
      const project = await createTrelloProject(
        boardsSelected,
        store.setLoadingProgress
      )
      setProject(project)
    }

    void run().catch(e => {
      logger.error('Failed to load from trello', e)
      e.message = `${Lang.msg.importFromTrello.failedToLoadBoards}: ${e.message}`
      store.setError(e)
    })
  }

  const showError = !!state.error
  const showLoading = !state.error && state.loading && !project
  const showLoadingDone = !state.error && state.loading && !!project
  const showImport =
    !state.error && !state.loading && boards && state.fullName.length > 0

  if (!state.open) {
    return null
  }

  return (
    <Popup
      autoHeight
      width={740}
      close={() => store.close()}
      title={Lang.msg.importFromTrello.importFromTrello}
      footer={
        state.selectedBoards.length > 0 &&
        !state.loading && (
          <Button onClick={onImportFromTrelloClick}>
            {Lang.msg.importFromTrello.import}
          </Button>
        )
      }
    >
      {showError && <div>{state.error?.message}</div>}
      {showLoading && (
        <TwCenter>
          <>
            <Loading />
            {state.loadingProgress > 0 && (
              <ProgressBar
                value={state.loadingProgress}
                maxValue={100}
                className="absolute w-200 mt-48"
              />
            )}
          </>
        </TwCenter>
      )}
      {showLoadingDone && (
        <div className="flex justify-center mt-48">
          <div className="flex flex-col justify-center text-16">
            {Lang.msg.importFromTrello.success}
            {!!project && (
              <LinkButton
                onClick={() => PageService.goToProject(project)}
                className="mx-auto"
              >
                {project.title}
              </LinkButton>
            )}
          </div>
        </div>
      )}
      {showImport && (
        <>
          {Lang.msg.importFromTrello.loggedInto}{' '}
          <span className="font-semibold">{state.fullName}</span>
          {'. '}
          {Lang.msg.importFromTrello.selectBoards}
          {boards.length > 3 && (
            <div className="flex flex-row">
              {!!state.selectedBoards.length && (
                <LinkButton onClick={store.removeSelection} className="mr-24">
                  {Lang.msg.importFromTrello.selectRemove}
                </LinkButton>
              )}
              {state.selectedBoards.length < boards.length && (
                <LinkButton onClick={store.selectAll}>
                  {Lang.msg.importFromTrello.selectAll}
                </LinkButton>
              )}
            </div>
          )}
          <div className="flex flex-wrap mt-8">
            {boards.map((board, i) => {
              const isSelected = state.selectedBoards.includes(board.value)
              return (
                <TwListItem
                  $selected={isSelected}
                  key={i}
                  onClick={() => store.toggleBoardSelected(board)}
                  style={{
                    backgroundImage: board.backgroundImage
                      ? `url(${board.backgroundImage})`
                      : undefined,
                    backgroundColor: board.backgroundColor,
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: 'cover'
                  }}
                >
                  {isSelected && (
                    <div className="absolute top-4 right-4 text-accent-old">
                      <SelectedIcon />
                    </div>
                  )}
                  <span className="break-words">{board.label}</span>
                </TwListItem>
              )
            })}
          </div>
          <div className="mt-20">
            {Lang.msg.importFromTrello.boardsSelected}{' '}
            <span className="font-semibold">{state.selectedBoards.length}</span>
          </div>
        </>
      )}
    </Popup>
  )
})

export const TRELLO_UNSELECTED_KEY = 'TRELLO_BOARDS_UNSELECTED'
